// Stroke-based equipment icons. Replaces the catalog's emoji glyphs
// in the picker tiles + equipment list so the UI has a consistent,
// designed look instead of the OS emoji palette (which differs across
// platforms and reads as "AI default"). Single colour (currentColor)
// so each icon picks up the surrounding text colour — works on both
// the picker tiles and the list rows without per-theme overrides.
//
// Falls back to the catalog's `icon` string when a kind doesn't have
// a custom SVG yet (custom kinds added via the admin catalog editor).
// As we add new canonical kinds, add their icon here.

const stroke = {
  fill: 'none',
  stroke: 'currentColor',
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
};

// Each entry returns the inner SVG content (paths/shapes only). The
// outer <svg> is added once in the component below so size + stroke
// width can scale uniformly.
const ICONS = {
  return_pump: (
    <>
      <circle cx="12" cy="12" r="8" {...stroke} strokeWidth="1.5" />
      <path d="M12 7v5l3 2" {...stroke} strokeWidth="1.5" />
      <path d="M5 12a7 7 0 0 1 13-3" {...stroke} strokeWidth="1.5" />
      <path d="M18 6v3h-3" {...stroke} strokeWidth="1.5" />
    </>
  ),
  skimmer: (
    <>
      <rect x="7" y="3" width="10" height="14" rx="2" {...stroke} strokeWidth="1.5" />
      <path d="M9 10c1 0 1-2 2-2s1 2 2 2 1-2 2-2" {...stroke} strokeWidth="1.5" />
      <circle cx="10" cy="7" r="0.7" fill="currentColor" />
      <circle cx="13.5" cy="6" r="0.5" fill="currentColor" />
      <path d="M9 17v3M15 17v3" {...stroke} strokeWidth="1.5" />
    </>
  ),
  heater: (
    <>
      <rect x="4" y="6" width="16" height="12" rx="2" {...stroke} strokeWidth="1.5" />
      <path d="M7 9c0 3 2 3 2 6M11 9c0 3 2 3 2 6M15 9c0 3 2 3 2 6" {...stroke} strokeWidth="1.5" />
    </>
  ),
  chiller: (
    <>
      <path d="M12 3v18M3 12h18M5.5 5.5l13 13M18.5 5.5l-13 13" {...stroke} strokeWidth="1.5" />
      <path d="M12 6l-2-2M12 6l2-2M12 18l-2 2M12 18l2 2" {...stroke} strokeWidth="1.5" />
      <path d="M6 12l-2-2M6 12l-2 2M18 12l2-2M18 12l2 2" {...stroke} strokeWidth="1.5" />
    </>
  ),
  wavemaker: (
    <>
      <path d="M3 8c2 0 2-2 4-2s2 2 4 2 2-2 4-2 2 2 4 2 2-2 2-2" {...stroke} strokeWidth="1.5" />
      <path d="M3 13c2 0 2-2 4-2s2 2 4 2 2-2 4-2 2 2 4 2 2-2 2-2" {...stroke} strokeWidth="1.5" />
      <path d="M3 18c2 0 2-2 4-2s2 2 4 2 2-2 4-2 2 2 4 2 2-2 2-2" {...stroke} strokeWidth="1.5" />
    </>
  ),
  powerhead: (
    <>
      <circle cx="12" cy="12" r="9" {...stroke} strokeWidth="1.5" />
      <circle cx="12" cy="12" r="1.5" fill="currentColor" />
      <path
        d="M12 4c3 2 3 5 0 8M20 12c-2-3-5-3-8 0M12 20c-3-2-3-5 0-8M4 12c2 3 5 3 8 0"
        {...stroke}
        strokeWidth="1.5"
      />
    </>
  ),
  lighting: (
    <>
      <circle cx="12" cy="12" r="4" {...stroke} strokeWidth="1.5" />
      <path
        d="M12 2v3M12 19v3M2 12h3M19 12h3M5 5l2 2M17 17l2 2M5 19l2-2M17 7l2-2"
        {...stroke}
        strokeWidth="1.5"
      />
    </>
  ),
  uv_sterilizer: (
    <>
      <rect x="7" y="3" width="10" height="18" rx="2" {...stroke} strokeWidth="1.5" />
      <path d="M13 8l-3 5h4l-3 5" {...stroke} strokeWidth="1.5" />
    </>
  ),
  roller_mat: (
    <>
      <ellipse cx="12" cy="6" rx="8" ry="2" {...stroke} strokeWidth="1.5" />
      <path d="M4 6v9M20 6v9" {...stroke} strokeWidth="1.5" />
      <ellipse cx="12" cy="15" rx="8" ry="2" {...stroke} strokeWidth="1.5" />
      <path d="M8 17v3M16 17v3" {...stroke} strokeWidth="1.5" />
    </>
  ),
  sock_holder: (
    <>
      <path d="M8 3h8v8l-2 2v6a2 2 0 0 1-4 0v-6l-2-2z" {...stroke} strokeWidth="1.5" />
      <path d="M8 8h8" {...stroke} strokeWidth="1.5" />
    </>
  ),
  refugium: (
    <>
      <path d="M5 21c0-8 6-14 14-14 0 8-6 14-14 14z" {...stroke} strokeWidth="1.5" />
      <path d="M5 21c4-4 8-6 13-7" {...stroke} strokeWidth="1.5" />
    </>
  ),
  reactor: (
    <>
      <path d="M10 3h4v6l5 9a2 2 0 0 1-2 3H7a2 2 0 0 1-2-3l5-9V3z" {...stroke} strokeWidth="1.5" />
      <path d="M10 3h4" {...stroke} strokeWidth="1.5" />
      <path d="M7.5 14h9" {...stroke} strokeWidth="1.5" />
    </>
  ),
  ato: (
    <>
      <path d="M12 3l5 7a6 6 0 1 1-10 0z" {...stroke} strokeWidth="1.5" />
      <path d="M9 14c0 1 1 2 3 2" {...stroke} strokeWidth="1.5" />
    </>
  ),
  rodi: (
    <>
      <rect x="4" y="4" width="16" height="3.5" rx="0.5" {...stroke} strokeWidth="1.5" />
      <rect x="4" y="8.5" width="16" height="3.5" rx="0.5" {...stroke} strokeWidth="1.5" />
      <rect x="4" y="13" width="16" height="3.5" rx="0.5" {...stroke} strokeWidth="1.5" />
      <rect x="4" y="17.5" width="16" height="3.5" rx="0.5" {...stroke} strokeWidth="1.5" />
    </>
  ),
  mixing_station: (
    <>
      <path d="M5 7h14l-1.5 12a2 2 0 0 1-2 2h-7a2 2 0 0 1-2-2z" {...stroke} strokeWidth="1.5" />
      <path d="M5 7c0-2 3-4 7-4s7 2 7 4" {...stroke} strokeWidth="1.5" />
      <path d="M9 11v6M12 11v6M15 11v6" {...stroke} strokeWidth="1.5" />
    </>
  ),
  // Every controller (Apex / HYDROS / TankSensei / ProfiLux / GHL /
  // other) shares the same chip glyph so the picker reads as one
  // family. Brand differentiation lives in the chip row below.
  controller_apex: <ControllerIcon />,
  controller_hydros: <ControllerIcon />,
  controller_aquasensei: <ControllerIcon />,
  controller_profilux: <ControllerIcon />,
  controller_ghl: <ControllerIcon />,
  controller_other: <ControllerIcon />,
  dosing: (
    <>
      <path d="M14 3l7 7-2 2-1-1-9 9H6v-3l9-9-1-1z" {...stroke} strokeWidth="1.5" />
      <path d="M13 7l4 4" {...stroke} strokeWidth="1.5" />
    </>
  ),
  auto_feeder: (
    <>
      <path d="M3 12a9 9 0 0 1 18 0" {...stroke} strokeWidth="1.5" />
      <path d="M2 12h20" {...stroke} strokeWidth="1.5" />
      <path d="M5 12l1 6a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2l1-6" {...stroke} strokeWidth="1.5" />
      <circle cx="9" cy="8" r="0.7" fill="currentColor" />
      <circle cx="13" cy="6.5" r="0.7" fill="currentColor" />
      <circle cx="15.5" cy="8.5" r="0.7" fill="currentColor" />
    </>
  ),
  // Standalone probes share one shape — a long rod with a bulb tip.
  sensor_ph: <ProbeIcon label="pH" />,
  sensor_orp: <ProbeIcon label="ORP" />,
  sensor_cond: <ProbeIcon label="EC" />,
  other: (
    <>
      <path
        d="M14.5 4a4 4 0 0 0-5 5L4 14.5l5 5L14.5 14a4 4 0 0 0 5-5z"
        {...stroke}
        strokeWidth="1.5"
      />
      <circle cx="16.5" cy="7.5" r="1" fill="currentColor" />
    </>
  ),
};

function ControllerIcon() {
  return (
    <>
      <rect x="5" y="5" width="14" height="14" rx="2" {...stroke} strokeWidth="1.5" />
      <rect x="9" y="9" width="6" height="6" rx="0.5" {...stroke} strokeWidth="1.5" />
      <path
        d="M9 3v2M12 3v2M15 3v2M9 19v2M12 19v2M15 19v2M3 9h2M3 12h2M3 15h2M19 9h2M19 12h2M19 15h2"
        {...stroke}
        strokeWidth="1.5"
      />
    </>
  );
}

function ProbeIcon({ label }) {
  return (
    <>
      <path d="M11 3h2v9h-2z" {...stroke} strokeWidth="1.5" />
      <circle cx="12" cy="17" r="4" {...stroke} strokeWidth="1.5" />
      <text
        x="12"
        y="18.5"
        textAnchor="middle"
        fontSize="3.4"
        fontFamily="'Oswald', sans-serif"
        fontWeight="600"
        fill="currentColor"
      >
        {label}
      </text>
    </>
  );
}

export function EquipmentIcon({ kind, size = 28, fallback }) {
  const inner = ICONS[kind];
  if (!inner) {
    // Catalog-supplied emoji glyph (admin-added kinds or legacy).
    return (
      <span aria-hidden="true" style={{ fontSize: size * 0.82, lineHeight: 1 }}>
        {fallback || '🔧'}
      </span>
    );
  }
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      aria-hidden="true"
      style={{ display: 'block' }}
    >
      {inner}
    </svg>
  );
}
